import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { PersonalAccountExternalUserService } from '@zaoblako/web/core/services/lk/personal-account-external-user.service';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import type { DropdownBaseQueryInterface } from '@zaoblako/shared/common/dtos/dropdown-base-query.dto';

import { finalize } from 'rxjs/operators';

interface ExternalUserWithStatus extends ExternalUserFindInterface {
  activePassStatus?: string;
}

@Component({
  selector: 'crm-personal-account-external-user-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SpinnerComponent],
  templateUrl: './personal-account-external-user-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PersonalAccountExternalUserSingleSelectComponent implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public externalUsers: ExternalUserWithStatus[] = [];
  public queryParams: DropdownBaseQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public selectionLoading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedExternalUser: ExternalUserWithStatus;
  @Input() public set selectedExternalUser(selectedExternalUser: ExternalUserWithStatus) {
    if (this.customSearch) {
      this._selectedExternalUser = selectedExternalUser;
      return;
    }

    if (this.externalUsers.some((externalUser) => externalUser.id === selectedExternalUser?.id)) {
      this._selectedExternalUser = this.externalUsers.find((externalUser) => externalUser.id === selectedExternalUser.id);
      return;
    }

    if (selectedExternalUser?.id) {
      const id = typeof selectedExternalUser.id === 'string' ? +selectedExternalUser.id : selectedExternalUser.id;
      this.resolveSelectedExternalUserById(id);
    }
  }

  @Output() public selectedExternalUserChange = new EventEmitter<ExternalUserWithStatus>();

  @Input() public customSearch = false;

  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = false;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = false;
  @Input() public placeholder = '\u00A0';
  @Input() public appendTo: string;

  @Input() public set setExternalUserId(id: number) {
    if (id === null || id === undefined) {
      return;
    }

    let _id = id;
    if (typeof id === 'string') {
      _id = +id;
    }

    if (this.externalUsers.some((externalUser) => externalUser.id === _id)) {
      this._selectedExternalUser = this.externalUsers.find((externalUser) => externalUser.id === _id);
      return;
    }

    this.resolveSelectedExternalUserById(_id);
  }

  public constructor(
    private readonly personalAccountExternalUserService: PersonalAccountExternalUserService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.personalAccountExternalUserService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          this.externalUsers = this.externalUsers?.length
            ? this.externalUsers.concat(
                data.items.filter((item) => !this.externalUsers.some((externalUser) => externalUser.id === item.id))
              )
            : data.items.filter((item) => !this.externalUsers.some((externalUser) => externalUser.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public onSelect = (externalUser: ExternalUserWithStatus): void => {
    this.selectedExternalUserChange.emit(externalUser);
  };

  private resolveSelectedExternalUserById = (id: number): void => {
    this.selectionLoading = true;
    this.cdRef.markForCheck();
    this.personalAccountExternalUserService
      .findById<ExternalUserWithStatus>(id)
      .pipe(
        finalize(() => {
          this.selectionLoading = false;
          this.cdRef.markForCheck();
        })
      )
      .subscribe({
        next: (_externalUser) => {
          if (!this.externalUsers.some((externalUser) => externalUser.id === _externalUser.id)) {
            this.externalUsers = this.externalUsers.concat(_externalUser);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedExternalUser = _externalUser;
            this.cdRef.markForCheck();
          }, 30);
        }
      });
  };

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller?.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.externalUsers.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.personalAccountExternalUserService.findWithPagination(this.queryParams).subscribe({
                next: (data) => {
                  this.externalUsers = this.externalUsers.concat(
                    data.items.filter((item) => !this.externalUsers.some((externalUser) => externalUser.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 50);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.personalAccountExternalUserService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedExternalUser?.id) && this._selectedExternalUser) {
            this.externalUsers = data.items.concat(this._selectedExternalUser);
          } else {
            this.externalUsers = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }
}
